<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Clientes"></navbar-dashboard>
      <div class="ew-view">
        <page-header title="Clientes" subtitle="Datos de empresa, crédito, tarifas y usuarios de cada cliente."></page-header>

        <div class="card ew-filters mb-3">
          <div class="form-row">
            <div class="col-12 col-md-8 form-group">
              <label for="client-search">Buscar</label>
              <div class="ew-search">
                <i class="bi bi-search"></i>
                <input id="client-search" type="search" class="form-control" v-model="filters.search"
                  @input="handleFilterChange" placeholder="Nombre, correo o nombre interno del cliente">
              </div>
            </div>
            <div class="col-12 col-md-4 form-group">
              <label for="client-status">Estatus</label>
              <select id="client-status" class="custom-select" v-model="filters.active" @change="handleFilterChange">
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>
        </div>

        <div class="ew-table-scroll">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Nombre interno</th>
                <th>Teléfono</th>
                <th>Crédito</th>
                <th class="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody v-if="listLoading && !clients.length">
              <tr v-for="n in 6" :key="'sk-' + n">
                <td v-for="c in 5" :key="'skc-' + c"><span class="ew-skeleton"></span></td>
              </tr>
            </tbody>
            <tbody v-else-if="clients.length">
              <tr v-for="client in clients" :key="client._id">
                <td>
                  <div class="ew-party ew-party--wide">
                    <span class="ew-party-name">{{ [client.firstName, client.lastName].filter(Boolean).join(' ') | dash }}</span>
                    <span class="ew-party-place">{{ client.email | dash }}</span>
                  </div>
                </td>
                <td>{{ client.company && client.company.internalClientName | dash }}</td>
                <td class="text-nowrap">{{ client.phone ? client.phone.trim() : '' | dash }}</td>
                <td>
                  <span class="badge" :class="creditBadge(client)">{{ creditStatus(client) }}</span>
                </td>
                <td class="text-right text-nowrap">
                  <button class="btn btn-sm btn-outline-secondary ew-icon-btn mr-1" @click="openEditModal(client)"
                    aria-label="Editar cliente" v-b-tooltip.hover.top="'Editar'">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <b-dropdown right no-caret size="sm" variant="outline-secondary" class="ew-row-menu"
                    toggle-class="ew-icon-btn" :popper-opts="{ positionFixed: true }">
                    <template #button-content>
                      <i class="bi bi-three-dots"></i><span class="sr-only">Más acciones</span>
                    </template>
                    <b-dropdown-header>Tarifas</b-dropdown-header>
                    <b-dropdown-item @click="openViewTariffModal(client)">
                      <i class="bi bi-table mr-2"></i> Ver tarifas y recargos
                    </b-dropdown-item>
                    <b-dropdown-item @click="openUploadTariffModal(client)">
                      <i class="bi bi-upload mr-2"></i> Cargar tabuladores
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-header>Usuarios</b-dropdown-header>
                    <b-dropdown-item @click="openAssignedUsersModal(client)">
                      <i class="bi bi-people mr-2"></i> Usuarios asignados
                    </b-dropdown-item>
                    <b-dropdown-item @click="showAssignModal(client)">
                      <i class="bi bi-person-plus mr-2"></i> Asignar usuario
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5" class="p-0">
                  <empty-state icon="bi bi-building" title="Sin clientes para mostrar"
                    :text="filters.search || filters.active ? 'No encontramos clientes con esos filtros.' : 'Los clientes aparecerán aquí cuando se registren.'"></empty-state>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <table-pagination :page="pagination.page" :total-pages="pagination.totalPages"
          :per-page="Number(pagination.limit)" :per-page-options="[20, 25, 50, 100]"
          @change="changePage" @per-page="setPerPage" />

        <b-modal id="viewTariffModal" ref="viewTariffModal"
          :title="`Tarifas de ${selectedClient.firstName} ${selectedClient.lastName}`" size="xl" centered hide-footer>
          <div v-if="tariffsLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando tarifas...</span>
            </div>
            <p class="mt-2">Cargando datos de tarifas...</p>
          </div>
          <div v-else>
            <b-tabs content-class="mt-3">
              <b-tab title="Tarifas Base" active>
                <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                  <table class="table table-sm table-striped table-bordered"
                    v-b-tooltip.hover.top="'Tabla detallada de tarifas base por zona y límite de peso/volumen'">
                    <thead class="thead-light sticky-top">
                      <tr>
                        <th>Paquetería</th>
                        <th>Servicio</th>
                        <th>Límite Kg</th>
                        <th>Límite Vol.</th>
                        <th v-for="zone in zones" :key="zone">{{ zone }} (MXN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="rate in clientTariffs.rates" :key="rate._id">
                        <td>{{ rate.carrier || '-' }}</td>
                        <td>{{ rate.service || '-' }}</td>
                        <td>{{ rate.weightLimitKg || 'N/A' }}</td>
                        <td>{{ rate.volumeLimit || 'N/A' }}</td>
                        <td v-for="zone in zones" :key="zone">
                          {{ rate.ratesByZone ? (rate.ratesByZone[zone] || 'N/A') : 'N/A' }}
                        </td>
                      </tr>
                      <tr v-if="clientTariffs.rates.length === 0">
                        <td :colspan="4 + zones.length" class="text-center text-muted">No se encontraron tarifas base.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-tab>

              <b-tab title="Recargos">
                <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                  <table class="table table-sm table-striped table-bordered"
                    v-b-tooltip.hover.top="'Listado de recargos adicionales aplicables al cliente'">
                    <thead class="thead-light sticky-top">
                      <tr>
                        <th>Nombre del Recargo</th>
                        <th>Carrier</th>
                        <th>Nombre catalogo</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(surcharge, index) in clientTariffs.surcharges" :key="index">
                        <td>{{ surcharge.surchargeName || '-' }}</td>
                        <td>{{ surcharge.carrier || '-' }}</td>
                        <td>{{ surcharge.type || '-' }}</td>
                        <td>{{ surcharge.cost || '0' }}</td>
                      </tr>
                      <tr v-if="clientTariffs.surcharges.length === 0">
                        <td colspan="4" class="text-center text-muted">No se encontraron recargos.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-tab>

              <b-tab title="Tarifas Internacionales">
                <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                  <table class="table table-sm table-striped table-bordered"
                    v-b-tooltip.hover.top="'Tabla de tarifas por país destino y límite de peso'">
                    <thead class="thead-light sticky-top">
                      <tr>
                        <th>Paquetería</th>
                        <th>Servicio</th>
                        <th>Límite Kg</th>
                        <th v-for="(price, country) in flattenCountryRates(clientTariffs.internationalRates)"
                          :key="country">{{ country }} (MXN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="rate in clientTariffs.internationalRates" :key="rate._id">
                        <td>{{ rate.carrier || '-' }}</td>
                        <td>{{ rate.service || '-' }}</td>
                        <td>{{ rate.weightLimitKg || 'N/A' }}</td>
                        <td v-for="(price, country) in flattenCountryRates(clientTariffs.internationalRates)"
                          :key="country">
                          {{ rate.ratesByCountry ? (rate.ratesByCountry[country] || 'N/A') : 'N/A' }}
                        </td>
                      </tr>
                      <tr v-if="clientTariffs.internationalRates.length === 0">
                        <td colspan="3" class="text-center text-muted">No se encontraron tarifas internacionales.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </b-modal>

        <b-modal id="uploadTariffModal" ref="uploadTariffModal" title="Subir Tabuladores" size="md" centered
          hide-footer>
          <p>Carga cada archivo Excel por separado para el cliente <strong>{{
            selectedClient.firstName }} {{ selectedClient.lastName }}</strong>.</p>
          <hr class="mt-2 mb-3" />
          <b-alert :show="!!uploadSuccessMessage" variant="success" dismissible>{{ uploadSuccessMessage }}</b-alert>
          <b-alert :show="!!uploadErrorMessage" variant="danger" dismissible>{{ uploadErrorMessage }}</b-alert>

          <b-tabs content-class="mt-3">
            <b-tab title="1. Tarifas Base" active>
              <b-form @submit.prevent="uploadTariffBase">
                <b-form-group label="Archivo de Tarifas Base (.xlsx)" label-for="file-input-base" class="mb-3">
                  <b-form-file id="file-input-base" v-model="selectedFile"
                    v-b-tooltip.hover.top="'Selecciona el archivo Excel con las tarifas base del cliente'"
                    placeholder="Elige el archivo de tarifas base..." required accept=".xlsx, .xls"></b-form-file>
                </b-form-group>
                <hr class="mt-4 mb-3" />
                <div class="d-flex justify-content-end">
                  <b-button type="button" variant="outline-secondary" class="mr-2" @click="hideUploadTariffModal"
                    v-b-tooltip.hover.top="'Cancelar y cerrar'" :disabled="uploading">Cancelar</b-button>
                  <b-button type="submit" variant="primary" :disabled="!selectedFile || uploading"
                    v-b-tooltip.hover.top="'Procesar y subir archivo de tarifas base'">
                    <span v-if="uploading" class="spinner-border spinner-border-sm mr-2" role="status"
                      aria-hidden="true"></span>
                    <span v-if="uploading"> Subiendo...</span>
                    <span v-else> Subir Tarifas Base</span>
                  </b-button>
                </div>
              </b-form>
            </b-tab>

            <b-tab title="2. Servicios Extra">
              <b-form @submit.prevent="uploadExtraServices">
                <b-form-group label="Archivo de Servicios Extra (.xlsx)" label-for="file-input-extra" class="mb-3">
                  <b-form-file id="file-input-extra" v-model="selectedExtraServiceFile"
                    v-b-tooltip.hover.top="'Selecciona el archivo Excel con los servicios extra'"
                    placeholder="Opcional: Elige el archivo de servicios extra..." accept=".xlsx, .xls"></b-form-file>
                </b-form-group>
                <hr class="mt-4 mb-3" />
                <div class="d-flex justify-content-end">
                  <b-button type="button" variant="outline-secondary" class="mr-2" @click="hideUploadTariffModal"
                    v-b-tooltip.hover.top="'Cancelar y cerrar'" :disabled="uploading">Cancelar</b-button>
                  <b-button type="submit" variant="primary" :disabled="!selectedExtraServiceFile || uploading"
                    v-b-tooltip.hover.top="'Procesar y subir archivo de servicios extra'">
                    <span v-if="uploading" class="spinner-border spinner-border-sm mr-2" role="status"
                      aria-hidden="true"></span>
                    <span v-if="uploading"> Subiendo...</span>
                    <span v-else> Subir Servicios Extra</span>
                  </b-button>
                </div>
              </b-form>
            </b-tab>

            <b-tab title="3. Tarifas Internacionales">
              <b-form @submit.prevent="uploadInternationalTariff">
                <b-form-group label="Archivo de Tarifas Internacionales (.xlsx)" label-for="file-input-international"
                  class="mb-3">
                  <b-form-file id="file-input-international" v-model="selectedInternationalFile"
                    v-b-tooltip.hover.top="'Selecciona el archivo Excel con las tarifas por país destino'"
                    placeholder="Elige el archivo de tarifas internacionales..." required
                    accept=".xlsx, .xls"></b-form-file>
                  <small class="form-text text-muted">Columnas esperadas: PAQUETERIA, SERVICIO, LIMITE EN KG, y una
                    columna por país destino con su código ISO (ej. US, CA, ES).</small>
                </b-form-group>
                <hr class="mt-4 mb-3" />
                <div class="d-flex justify-content-end">
                  <b-button type="button" variant="outline-secondary" class="mr-2" @click="hideUploadTariffModal"
                    v-b-tooltip.hover.top="'Cancelar y cerrar'" :disabled="uploading">Cancelar</b-button>
                  <b-button type="submit" variant="primary" :disabled="!selectedInternationalFile || uploading"
                    v-b-tooltip.hover.top="'Procesar y subir archivo de tarifas internacionales'">
                    <span v-if="uploading" class="spinner-border spinner-border-sm mr-2" role="status"
                      aria-hidden="true"></span>
                    <span v-if="uploading"> Subiendo...</span>
                    <span v-else> Subir Tarifas Internacionales</span>
                  </b-button>
                </div>
              </b-form>
            </b-tab>
          </b-tabs>
        </b-modal>

        <b-modal id="assigned-users-modal" ref="assignedUsersModal" title="Usuarios asignados al cliente" size="lg"
          centered hide-footer>
          <div class="table-responsive">
            <table class="table table-hover table-bordered mb-0"
              v-b-tooltip.hover.top="'Listado de usuarios asociados a este cliente'">
              <thead class="thead-light">
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in assignedUsers" :key="user._id">
                  <td>{{ user.firstName }} {{ user.lastName }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.phone || '-' }}</td>
                  <td class="text-center">
                    <button class="btn btn-sm btn-outline-danger ew-icon-btn" @click="confirmRemoveUser(user)"
                      aria-label="Quitar usuario" v-b-tooltip.hover.top="'Quitar del cliente'">
                      <i class="bi bi-person-dash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="assignedUsers.length === 0">
                  <td colspan="4" class="text-center text-muted">No hay usuarios asignados a este cliente.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-modal>

        <b-modal ref="assignUser" hide-footer title="Asignar Usuario a Cliente">
          <form @submit.prevent="assignUser">
            <div class="form-group">
              <label>Cliente <sup>*</sup></label>
              <select class="form-control" v-model="selectedClientId" required disabled
                v-b-tooltip.hover.top="'Cliente seleccionado para la asignación'">
                <option v-for="client in clients" :key="client._id" :value="client._id">
                  {{ client.firstName }} {{ client.lastName }} ({{ client.email }})
                </option>
              </select>
            </div>
            <div class="form-group position-relative">
              <label>Usuario a asignar <sup>*</sup></label>
              <div class="form-control" @click="showUserDropdown = !showUserDropdown" style="cursor: pointer;">
                {{ selectedUserDisplay || 'Selecciona un usuario...' }}
              </div>

              <div v-show="showUserDropdown" class="list-dropdown-container shadow" @scroll="onUserScroll"
                style="position: absolute; width: 100%; max-height: 200px; overflow-y: auto; background: white; z-index: 1000;">
                <div v-for="user in availableUsers" :key="user._id" @click="selectUser(user)"
                  class="p-2 list-item-option" style="cursor: pointer;">
                  {{ user.firstName }} {{ user.lastName }} ({{ user.email }})
                </div>
                <div v-if="isLoading" class="p-2 text-center text-muted">Cargando...</div>
              </div>
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideAssignModal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="!selectedUserId || !selectedClientId"
                v-b-tooltip.hover.top="'Confirmar la asignación del usuario al cliente'">
                Asignar Usuario
              </button>
            </div>
          </form>
        </b-modal>

        <b-modal id="editUserModal" ref="editUser" title="Editar Cliente" size="lg" centered hide-footer>
          <template #modal-header>
            <h5 class="mb-0">Editar {{ selectedClient.firstName }} {{ selectedClient.lastName }}</h5>
            <button type="button" class="close" aria-label="Cerrar" @click="$refs.editUser.hide()">&times;</button>
          </template>

          <b-form @submit.prevent="updateCompania">
            <h6 class="mb-2">Datos de la Empresa</h6>
            <b-form-group label="Nombre interno" label-for="internalName" class="mb-2">
              <b-form-input id="internalName" v-model="selectedClient.company.internalClientName"
                v-b-tooltip.hover.top="'Nombre descriptivo interno del cliente'" placeholder="Ej. Envipaq Norte" />
            </b-form-group>

            <b-form-group label="Número interno" label-for="internalNumber" class="mb-2">
              <b-form-input id="internalNumber" v-model="selectedClient.company.internalClientNumber"
                v-b-tooltip.hover.top="'Identificador interno único del cliente'" placeholder="Ej. 001-A" />
            </b-form-group>

            <b-form-group label="Dirección del almacén" label-for="warehouseAddress" class="mb-3">
              <b-form-input id="warehouseAddress" v-model="selectedClient.company.defaultWarehouseAddress"
                v-b-tooltip.hover.top="'Dirección física principal del almacén del cliente'"
                placeholder="Ej. Calle 123, Colonia Centro, CDMX" />
            </b-form-group>

            <hr />

            <h6 class="mt-3 mb-2">Datos de facturación</h6>
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="Nombre o razón social">
                  <b-form-input v-model="selectedClient.company.billingData.name" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Razón social para facturación'"
                    placeholder="Ej. Transportes Envipaq S.A. de C.V." />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="RFC">
                  <b-form-input v-model="selectedClient.company.billingData.rfc" autocomplete="off" data-lpignore="true"
                    v-b-tooltip.hover.top="'Registro Federal de Contribuyentes'" placeholder="Ej. TEN123456AB1" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Teléfono">
                  <b-form-input v-model="selectedClient.company.billingData.phone" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Teléfono de contacto para facturación'"
                    placeholder="Ej. 444 123 4567" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Correo electrónico">
                  <b-form-input v-model="selectedClient.company.billingData.email" type="email" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Correo para envío de facturas'"
                    placeholder="Ej. facturacion@empresa.com" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Dirección fiscal">
                  <b-form-input v-model="selectedClient.company.billingData.address" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Domicilio fiscal completo'"
                    placeholder="Ej. Av. Reforma 123" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Colonia">
                  <b-form-input v-model="selectedClient.company.billingData.colony" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Colonia del domicilio fiscal'"
                    placeholder="Ej. Centro" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Código postal">
                  <b-form-input v-model="selectedClient.company.billingData.postal_code" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Código postal fiscal'" placeholder="Ej. 78000" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Ciudad / Estado">
                  <b-form-input v-model="selectedClient.company.billingData.state_city" autocomplete="off"
                    data-lpignore="true" v-b-tooltip.hover.top="'Ciudad y estado fiscal'"
                    placeholder="Ej. San Luis Potosí, S.L.P." />
                </b-form-group>
              </b-col>
            </b-row>

            <hr />

            <h6 class="mt-3 mb-2">Estado de crédito</h6>
            <b-row>
              <b-col cols="12" md="4">
                <b-form-group label="Estado del crédito">
                  <b-form-select v-model="selectedClient.company.creditStatus.status"
                    v-b-tooltip.hover.top="'Define si el crédito está activo, suspendido o en revisión'"
                    :options="['Activo', 'Suspendido', 'Revisión']" required />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Límite de crédito">
                  <b-form-input type="number" v-model.number="selectedClient.company.creditStatus.creditLimit"
                    v-b-tooltip.hover.top="'Monto máximo de crédito permitido'" placeholder="Ej. 50000" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Días de crédito">
                  <b-form-input type="number" v-model.number="selectedClient.company.creditStatus.creditDays"
                    v-b-tooltip.hover.top="'Número de días para liquidar el crédito'" placeholder="Ej. 30" />
                </b-form-group>
              </b-col>
            </b-row>
            <hr />

            <!-- SECCIÓN DE DOCUMENTOS DE LA EMPRESA -->
            <h6 class="mt-3 mb-2 text-dark fw-bold">
              <i class="bi bi-file-earmark-text text-primary mr-1"></i> Documentación del cliente
            </h6>
            <b-row class="mb-3">
              <b-col cols="12" md="6" v-for="docDef in availableDocuments" :key="docDef.name" class="mb-3">
                <div class="p-3 border rounded bg-light h-100 d-flex flex-column justify-content-between">
                  <div>
                    <label class="fw-bold mb-1 text-capitalize">{{ docDef.label }}</label>

                    <!-- Enlace para ver el documento actual si existe -->
                    <div v-if="getDocUrl(docDef.name)" class="mb-2">
                      <a :href="getDocUrl(docDef.name)" target="_blank" class="btn btn-sm btn-outline-primary">
                        <i class="bi bi-eye mr-1"></i> Ver archivo actual
                      </a>
                    </div>
                    <div v-else class="text-muted small mb-2">
                      <em>Sin archivo cargado</em>
                    </div>
                  </div>

                  <!-- Input para subir o reemplazar el archivo -->
                  <div>
                    <label class="form-label small text-muted mb-1">Subir / Reemplazar archivo:</label>
                    <b-form-file v-model="newFiles[docDef.name]" :name="docDef.name"
                      placeholder="Elige un archivo o arrástralo aquí..." drop-placeholder="Suelta el archivo aquí..."
                      accept=".pdf,.png,.jpg,.jpeg" size="sm"
                      @change="(e) => handleFileChange(e, docDef.name)"></b-form-file>
                  </div>
                </div>
              </b-col>
            </b-row>
            <hr />
            <h6 class="mt-3 mb-2 text-dark fw-bold"><i class="bi bi-bell text-primary mr-1"></i> Preferencias de notificación
            </h6>
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="Correo electrónico (Adicional 1)">
                  <b-form-input v-model="selectedClient.emailPlus1" type="email"
                    v-b-tooltip.hover.top="'Correo electrónico de contacto alternativo'"
                    placeholder="Ej. contacto1@empresa.com" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Correo electrónico (Adicional 2)">
                  <b-form-input v-model="selectedClient.emailPlus2" type="email"
                    v-b-tooltip.hover.top="'Correo electrónico de contacto alternativo'"
                    placeholder="Ej. contacto2@empresa.com" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <div class="p-3 border rounded bg-light d-flex align-items-center justify-content-between">
                  <div>
                    <label class="form-check-label fw-bold mb-0" style="cursor: pointer;" for="notifyDocs">
                      Enviar correos de documentación
                    </label>
                    <small class="form-text text-muted d-block mt-1">
                      El administrador recibirá alertas por correo con los detalles del envío cada vez que se documente
                      un
                      paquete.
                    </small>
                  </div>
                  <div class="form-check form-switch fs-5 mb-0 pe-2">
                    <input class="form-check-input" type="checkbox" id="notifyDocs"
                      v-model="selectedClient.notifyDocumentation"
                      v-b-tooltip.hover.top="'Activar/Desactivar notificaciones automáticas de nuevos envíos'"
                      style="cursor: pointer; width: 2.3em; height: 1.15em;" />
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col cols="12">
                <div class="p-3 border rounded bg-light d-flex align-items-center justify-content-between">
                  <div>
                    <label class="form-check-label fw-bold mb-0" style="cursor: pointer;" for="canQuoteInternational">
                      Envíos internacionales
                    </label>
                    <small class="form-text text-muted d-block mt-1">
                      Permite a este cliente cotizar y generar guías internacionales (requiere tener cargado un
                      tabulador de tarifas internacionales).
                    </small>
                  </div>
                  <div class="form-check form-switch fs-5 mb-0 pe-2">
                    <input class="form-check-input" type="checkbox" id="canQuoteInternational"
                      v-model="selectedClient.canQuoteInternational"
                      v-b-tooltip.hover.top="'Activar/Desactivar la cotización y generación de guías internacionales para este cliente'"
                      style="cursor: pointer; width: 2.3em; height: 1.15em;" />
                  </div>
                </div>
              </b-col>
            </b-row>
            <hr />

            <div class="d-flex justify-content-end mt-3">
              <b-button variant="outline-secondary" class="mr-2" @click="hideEditModal()">Cancelar</b-button>
              <b-button type="submit" variant="primary" :disabled="loading"
                v-b-tooltip.hover.top="'Guardar todos los cambios realizados en este formulario'">
                <span v-if="loading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                Guardar cambios
              </b-button>
            </div>
          </b-form>
        </b-modal>
      </div>
    </div>
  </div>
</template>
<script>
import Navbar from './Header/Navbar'
import axios from "axios";
import commons from '../commons'

export default {
  name: "ClientUsers",
  data() {
    return {
      availableDocuments: [
        { name: 'constancia_fiscal', label: 'Constancia Fiscal' },
        { name: 'comprobante_domicilio', label: 'Comprobante de Domicilio' },
        { name: 'identificacion_apoderado', label: 'Identificación del Apoderado' },
        { name: 'opinion_positiva', label: 'Opinión Positiva (32-D)' },
        { name: 'propuesta_firmada', label: 'Propuesta Firmada' }
      ],
      newFiles: {},
      clients: [],
      selectedClient: {
        firstName: "",
        lastName: "",
        notifyDocumentation: false, // <-- Inicialización en el esquema por defecto del data
        canQuoteInternational: false,
        company: {
          billingData: {
            name: "", rfc: "", phone: "", address: "", colony: "", postal_code: "", state_city: "", email: ""
          },
          accountStatus: { invoicesOnTime: 0, invoicesOverdue: 0 },
          creditStatus: { status: "Activo", creditLimit: 0, creditDays: 0 },
          internalClientNumber: "", internalClientName: "", defaultWarehouseAddress: ""
        },
      },
      selectedUserId: "",
      selectedClientId: "",
      assignedUsers: [],
      availableUsers: [],
      loading: false,
      success: false,
      //SELECT CON PAGINACION ROL USUARIO
      currentPage: 1,
      totalPages: 1,
      isLoading: false,
      showUserDropdown: false,
      selectedUserId: '',
      selectedUserDisplay: '',

      selectedFile: null,
      selectedExtraServiceFile: null,
      selectedInternationalFile: null,
      uploading: false,
      uploadSuccessMessage: "",
      uploadErrorMessage: "",

      clientTariffs: { rates: [], surcharges: [], internationalRates: [], tariffs: [] },
      tariffsLoading: false,
      pagination: {
        page: 1,
        limit: 20,
        totalPages: 1
      },
      filters: {
        search: '',
        active: ''
      },
      listLoading: false,
      filterTimeout: null,
    };
  },
  computed: {
    // Las columnas de zona ya no son fijas: además de las 8 zonas numéricas
    // domésticas (ZONA 1..8), un tabulador puede traer zonas por letra
    // (ZONA A..F, ej. FedEx internacional). Se unen las que de verdad
    // aparecen en las tarifas cargadas, igual que ya se hace para países
    // en flattenCountryRates.
    zones() {
      const zoneSet = new Set();
      (this.clientTariffs.rates || []).forEach((rate) => {
        Object.keys(rate.ratesByZone || {}).forEach((zone) => zoneSet.add(zone));
      });
      return Array.from(zoneSet).sort((a, b) => {
        const numA = a.replace(/\D/g, '');
        const numB = b.replace(/\D/g, '');
        if (numA && numB) return parseInt(numA, 10) - parseInt(numB, 10);
        if (!!numA !== !!numB) return numA ? -1 : 1;
        return a.localeCompare(b);
      });
    },
  },
  methods: {
    openAssignedUsersModal(client) {
      this.$refs.assignedUsersModal.show();
      this.selectedClientId = client._id;
      this.fetchClientUsers(client._id);
    },
    openEditModal(client) {
      this.selectedClientId = client._id;
      this.selectedClient = structuredClone(client);

      // Control preventivo si la propiedad no viene del backend inicialmente
      if (this.selectedClient.notifyDocumentation === undefined) {
        this.$set(this.selectedClient, 'notifyDocumentation', false);
      }
      if (this.selectedClient.canQuoteInternational === undefined) {
        this.$set(this.selectedClient, 'canQuoteInternational', false);
      }
      this.$refs.editUser.show();
    },
    hideEditModal() {
      this.$refs.editUser.hide();
      this.selectedClient = this.$options.data.apply(this).selectedClient;
      this.selectedClientId = "";
    },
    showAssignModal(client) {
      this.selectedClientId = client._id;
      this.fetchAvailableUsers();
      this.$refs.assignUser.show();
    },
    hideAssignModal() {
      this.$refs.assignUser.hide();
      this.selectedUserId = "";
      this.selectedClientId = "";
    },

    openUploadTariffModal(client) {
      this.selectedClientId = client._id;
      this.selectedClient = structuredClone(client);
      this.selectedFile = null;
      this.selectedExtraServiceFile = null;
      this.selectedInternationalFile = null;
      this.uploadSuccessMessage = "";
      this.uploadErrorMessage = "";
      this.$refs.uploadTariffModal.show();
    },
    hideUploadTariffModal() {
      this.$refs.uploadTariffModal.hide();
      this.selectedClientId = "";
      this.selectedClient = this.$options.data.apply(this).selectedClient;
      this.selectedFile = null;
      this.selectedExtraServiceFile = null;
      this.selectedInternationalFile = null;
      this.uploadSuccessMessage = "";
      this.uploadErrorMessage = "";
    },
    async uploadTariffBase() {
      if (!this.selectedFile || !this.selectedClientId) return;
      this.uploading = true;
      this.uploadSuccessMessage = "";
      this.uploadErrorMessage = "";
      try {
        const formData = new FormData();
        formData.append('tariffFile', this.selectedFile);
        formData.append('clientId', this.selectedClientId);
        const res = await axios.post(`${commons.apiURL}/api/v1/tariffs/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.uploadSuccessMessage = res.data.message || "Tarifas Base subidas correctamente.";
        this.selectedFile = null;
        this.fetchClients();
      } catch (err) {
        console.error("Error al subir tarifas base:", err.response || err);
        this.uploadErrorMessage = err.response && err.response.data && err.response.data.message
          ? `Error al subir Base: ${err.response.data.message}`
          : "Error de red o interno del servidor al procesar tarifas base.";
      } finally {
        this.uploading = false;
        setTimeout(() => { this.uploadSuccessMessage = ""; this.uploadErrorMessage = ""; }, 4000);
      }
    },
    async uploadExtraServices() {
      if (!this.selectedExtraServiceFile || !this.selectedClientId) return;
      this.uploading = true;
      this.uploadSuccessMessage = "";
      this.uploadErrorMessage = "";
      try {
        const formData = new FormData();
        formData.append('extraServiceFile', this.selectedExtraServiceFile);
        formData.append('clientId', this.selectedClientId);
        const res = await axios.post(`${commons.apiURL}/api/v1/tariffs/upload-extras`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.uploadSuccessMessage = res.data.message || "Servicios Extra subidos correctamente.";
        this.selectedExtraServiceFile = null;
      } catch (err) {
        console.error("Error al subir servicios extra:", err.response || err);
        this.uploadErrorMessage = err.response && err.response.data && err.response.data.message
          ? `Error al subir Extras: ${err.response.data.message}`
          : "Error de red o interno del servidor al procesar servicios extra.";
      } finally {
        this.uploading = false;
        setTimeout(() => { this.uploadSuccessMessage = ""; this.uploadErrorMessage = ""; }, 4000);
      }
    },
    openViewTariffModal(client) {
      this.selectedClientId = client._id;
      this.selectedClient = structuredClone(client);
      this.fetchTariffData(client._id);
      this.$refs.viewTariffModal.show();
    },
    async fetchTariffData(clientId) {
      this.tariffsLoading = true;
      this.clientTariffs = { rates: [], surcharges: [], internationalRates: [], tariffs: [] };
      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/tariffs/${clientId}`);
        this.clientTariffs.rates = res.data.rates || [];
        this.clientTariffs.surcharges = res.data.surcharges || [];
        this.clientTariffs.internationalRates = res.data.internationalRates || [];
        this.clientTariffs.tariffs = res.data.tariffs || [];
      } catch (err) {
        console.error("Error al obtener datos de tarifas:", err);
      } finally {
        this.tariffsLoading = false;
      }
    },
    // Une las claves de país de todas las líneas de tarifa internacional,
    // ya que a diferencia de las zonas (fijas) las columnas de país varían
    // según lo que se haya cargado en el Excel.
    flattenCountryRates(internationalRates) {
      const merged = {};
      (internationalRates || []).forEach((rate) => {
        Object.keys(rate.ratesByCountry || {}).forEach((country) => {
          merged[country] = true;
        });
      });
      return merged;
    },
    async uploadInternationalTariff() {
      if (!this.selectedInternationalFile || !this.selectedClientId) return;
      this.uploading = true;
      this.uploadSuccessMessage = "";
      this.uploadErrorMessage = "";
      try {
        const formData = new FormData();
        formData.append('internationalTariffFile', this.selectedInternationalFile);
        formData.append('clientId', this.selectedClientId);
        const res = await axios.post(`${commons.apiURL}/api/v1/tariffs/upload-international`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.uploadSuccessMessage = res.data.message || "Tarifas internacionales subidas correctamente.";
        this.selectedInternationalFile = null;
      } catch (err) {
        console.error("Error al subir tarifas internacionales:", err.response || err);
        this.uploadErrorMessage = err.response && err.response.data && err.response.data.message
          ? `Error al subir Internacionales: ${err.response.data.message}`
          : "Error de red o interno del servidor al procesar tarifas internacionales.";
      } finally {
        this.uploading = false;
        setTimeout(() => { this.uploadSuccessMessage = ""; this.uploadErrorMessage = ""; }, 4000);
      }
    },

    // === ENVÍO AL BACKEND MODIFICADO ===
    // Obtiene la URL completa del documento si ya fue guardado previamente
    getDocUrl(docName) {
      if (!this.selectedClient.company || !this.selectedClient.company.documents) return null;
      const found = this.selectedClient.company.documents.find(d => d.name === docName);
      if (!found || !found.fileUrl) return null;

      // Concatena con tu URL de la API del backend (ej. https://api.sacktechlogistics.com o http://localhost:3000)
      return `${commons.apiURL}/api${found.fileUrl}`;
    },

    // Obtiene el estatus del documento ("Pendiente", "Aprobado", etc.)
    getDocStatus(docName) {
      if (!this.selectedClient.company || !this.selectedClient.company.documents) return 'Pendiente';
      const found = this.selectedClient.company.documents.find(d => d.name === docName);
      return found ? found.status : 'Pendiente';
    },

    // Clases dinámicas para los badges de estatus
    getDocStatusClass(docName) {
      const status = this.getDocStatus(docName);
      if (status === 'Aprobado') return 'bg-success text-white';
      if (status === 'Rechazado') return 'bg-danger text-white';
      return 'bg-warning text-dark'; // Para "Pendiente"
    },

    // Captura el archivo seleccionado para mandarlo por FormData en tu método updateCompania
    handleFileChange(event, docName) {
      const file = event.target.files[0];
      if (file) {
        this.newFiles[docName] = file;
      }
    },
    async updateCompania() {
      try {
        this.loading = true;
        const formData = new FormData();

        // Adjuntar los datos generales de la compañía en formato JSON string como lo espera tu backend
        formData.append('company', JSON.stringify(this.selectedClient.company));
        formData.append('notifyDocumentation', this.selectedClient.notifyDocumentation);
        formData.append('emailPlus1', this.selectedClient.emailPlus1 || '');
        formData.append('emailPlus2', this.selectedClient.emailPlus2 || '');
        formData.append('canQuoteInternational', this.selectedClient.canQuoteInternational);

        // Adjuntar los archivos nuevos utilizando exactamente el fieldname correspondiente
        Object.keys(this.newFiles).forEach(key => {
          if (this.newFiles[key]) {
            formData.append(key, this.newFiles[key]);
          }
        });

        // Petición Axios a tu API
        await axios.put(`${commons.apiURL}/api/v1/compania/${this.selectedClient._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        this.$bvToast.toast('Cliente actualizado correctamente', { variant: 'success', solid: true });
        this.$refs.editUser.hide();
        this.fetchClients(); // Recargar tu listado
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Error al actualizar el cliente', { variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.pagination.page = 1;
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => this.fetchClients(), 300);
    },
    setPerPage(limit) {
      this.pagination.limit = limit;
      this.pagination.page = 1;
      this.fetchClients();
    },
    creditStatus(client) {
      const c = client.company && client.company.creditStatus;
      return (c && c.status) || 'Sin crédito';
    },
    creditBadge(client) {
      const status = this.creditStatus(client);
      if (status === 'Activo') return 'badge-success';
      if (status === 'Suspendido') return 'badge-danger';
      if (status === 'Revisión') return 'badge-warning';
      return 'badge-secondary';
    },
    confirmRemoveUser(user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
      this.$bvModal.msgBoxConfirm(`${name} dejará de estar asignado a este cliente.`, {
        title: 'Quitar usuario',
        okTitle: 'Quitar',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true
      }).then(ok => { if (ok) this.removeUser(user._id) })
    },
    async fetchClients() {
      this.listLoading = true;
      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/users`, {
          params: {
            rol: 'client,superadmin',
            page: this.pagination.page,
            limit: this.pagination.limit,
            filter: this.filters.search,
            active: this.filters.active
          }
        });

        this.clients = res.data.docs || [];
        this.pagination.totalPages = res.data.pages || 1;
      } catch (err) {
        console.error("Error al obtener clientes:", err);
      } finally {
        this.listLoading = false;
      }
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.pagination.totalPages) {
        this.pagination.page = newPage;
        this.fetchClients();
      }
    },
    async fetchClientUsers(selectedClientId) {
      if (!selectedClientId) return;
      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/client/${selectedClientId}/users`);
        this.assignedUsers = res.data.users || [];
      } catch (err) {
        console.error("Error al obtener usuarios del cliente:", err);
      }
    },
    async fetchAvailableUsers(page = 1) {
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/users`, {
          params: { rol: 'user,admin_add', page: page, limit: 10 }
        });

        const newUsers = res.data.docs || [];
        this.totalPages = res.data.pages;
        this.currentPage = page;

        if (page === 1) {
          this.availableUsers = newUsers;
        } else {
          this.availableUsers = [...this.availableUsers, ...newUsers];
        }
      } catch (err) {
        console.error("Error al obtener usuarios:", err);
      } finally {
        this.isLoading = false;
      }
    },
    onUserScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 5 && this.currentPage < this.totalPages) {
        this.fetchAvailableUsers(this.currentPage + 1);
      }
    },
    selectUser(user) {
      this.selectedUserId = user._id;
      this.selectedUserDisplay = `${user.firstName} ${user.lastName}`;
      this.showUserDropdown = false;
    },
    async assignUser() {
      if (!this.selectedClientId || !this.selectedUserId) return;
      try {
        await axios.patch(`${commons.apiURL}/api/v1/client/${this.selectedClientId}/assign-user/${this.selectedUserId}`);
        this.hideAssignModal();
        this.fetchClientUsers(this.selectedClientId);
      } catch (err) {
        console.error("Error al asignar usuario:", err);
      }
    },
    async removeUser(userId) {
      try {
        await axios.patch(`${commons.apiURL}/api/v1/client/remove-user/${userId}`);
        this.fetchClientUsers(this.selectedClientId);
      } catch (err) {
        console.error("Error al remover usuario:", err);
      }
    },
  },
  mounted() {
    this.fetchClients();
  },
  components: { 'navbar-dashboard': Navbar }
};
</script>
<style scoped>
.modal {
  background: rgba(0, 0, 0, 0.4);
}

.modal-content {
  border-radius: 10px;
}

.ew-party--wide {
  max-width: 280px;
}

.table thead.thead-light.sticky-top tr th {
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
</style>



// WEBPACK FOOTER //
// src/components/Clients.vue